import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import api from '../services/api'
import { formatCurrency } from '../utils/helpers'
import { Invoice, Order, Proposal } from '../types'

interface Line {
  id?: string
  description: string
  qty: number
  unit_price: number
  total_excl_tax: number
  vat_rate: number
  product_ref?: string
  product_label?: string
}

interface Props {
  id: string
  type: 'invoice' | 'order' | 'proposal'
  /** The bounding rect of the hovered row, used to position the popup */
  anchorRect: DOMRect
}

const MAX_PREVIEW = 5

/**
 * Floating popup showing line items for an invoice, order, or proposal.
 * Rendered at fixed position below the hovered list row.
 */
export default function LinePreviewPopup({ id, type, anchorRect }: Props) {
  const { t } = useTranslation()
  const [lines, setLines] = useState<Line[] | null>(null)
  const [loading, setLoading] = useState(true)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let mounted = true
    const fetchData = async () => {
      try {
        let data: Invoice | Order | Proposal
        if (type === 'invoice') data = await api.getInvoice(id)
        else if (type === 'order') data = await api.getOrder(id)
        else data = await api.getProposal(id)
        if (mounted) setLines(data?.lines ?? [])
      } catch {
        if (mounted) setLines([])
      } finally {
        if (mounted) setLoading(false)
      }
    }
    fetchData()
    return () => {
      mounted = false
    }
  }, [id, type])

  // Position: prefer below the row; if near the bottom of the viewport, flip above
  const viewportHeight = window.innerHeight
  const popupHeight = 240 // approximate
  const spaceBelow = viewportHeight - anchorRect.bottom
  const top = spaceBelow >= popupHeight ? anchorRect.bottom + 6 : anchorRect.top - popupHeight - 6

  const left = Math.max(8, Math.min(anchorRect.left, window.innerWidth - 500))

  const visible = lines ? lines.slice(0, MAX_PREVIEW) : []
  const extra = lines && lines.length > MAX_PREVIEW ? lines.length - MAX_PREVIEW : 0

  return (
    <div
      ref={ref}
      style={{ position: 'fixed', top, left, zIndex: 9999, minWidth: 360, maxWidth: 520 }}
      className="bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden animate-fade-in"
    >
      {loading ? (
        <div className="flex items-center gap-2 p-4 text-sm text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          {t('lineItems.previewLoading')}
        </div>
      ) : lines && lines.length === 0 ? (
        <p className="px-4 py-3 text-sm text-gray-400 italic">{t('lineItems.noLines')}</p>
      ) : (
        <div>
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left py-2 px-3 font-medium text-gray-500">
                  {t('lineItems.colRef')}
                </th>
                <th className="text-left py-2 px-3 font-medium text-gray-500">
                  {t('lineItems.colDescription')}
                </th>
                <th className="text-right py-2 px-3 font-medium text-gray-500">
                  {t('lineItems.colQty')}
                </th>
                <th className="text-right py-2 px-3 font-medium text-gray-500">
                  {t('lineItems.colTotal')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {visible.map((line, idx) => {
                const label = line.product_label || line.description || '—'
                const ref = line.product_ref || '—'
                return (
                  <tr key={line.id ?? idx} className="hover:bg-gray-50">
                    <td className="py-1.5 px-3 font-mono text-gray-500 whitespace-nowrap">{ref}</td>
                    <td className="py-1.5 px-3 text-gray-700 max-w-[200px] truncate" title={label}>
                      {label}
                    </td>
                    <td className="py-1.5 px-3 text-right text-gray-600">{line.qty}</td>
                    <td className="py-1.5 px-3 text-right font-medium text-gray-900">
                      {formatCurrency(line.total_excl_tax)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          {extra > 0 && (
            <p className="px-3 py-2 text-xs text-gray-400 italic border-t border-gray-100">
              {t('lineItems.moreLines', { count: extra })}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
